// Directional content swap for the step / day / week surfaces (Programmations week, Nutrition day, Séance exercise, Bilans and
// Bilan initial steps, Onboarding steps). Before (passe interactions 13/10): the surface carried `key={step}` and a CSS
// keyframe, so every rapid change remounted it and restarted the slide from 0, a visible jump. Now the SURFACE persists (only
// its content is keyed, so per-step inputs, focus and state behave as before) and this helper animates it with the Web
// Animations API: on a change while a slide is still running, it reads the live presented transform and opacity, cancels that
// animation and continues from there; otherwise it enters from the side of travel with the same offset, duration and easing
// as the former keyframe. Input is never blocked (no pointer-events change, no await). Reduced motion: opacity only, no
// transform at all (the former reduced-motion fade, same duration).
import { useLayoutEffect, useRef, type RefObject } from "react";

export interface SwapMotion {
  /** Horizontal entrance offset in px (sign given by the direction of travel). */
  offset: number;
  /** Duration in ms, and easing (a CSS timing function, or a custom property name such as "--ccn-ease"). */
  duration: number;
  easing: string;
  /** Reduced-motion fade duration in ms. */
  reducedDuration: number;
}

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Animates `ref`'s element each time `change` changes (and on mount), from the side `dir` (1, -1, or 0 for a plain fade). */
export function useDirectionalSwap(ref: RefObject<HTMLElement | null>, change: unknown, dir: number, m: SwapMotion) {
  const anim = useRef<Animation | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return;
    const prev = anim.current;
    const running = prev !== null && prev.playState === "running";
    // Live presented values of an interrupted slide, copied as STRINGS before cancelling it: getComputedStyle returns a LIVE
    // CSSOM declaration, so reading it after cancel() would give the post-cancel (resting) values, not the presented ones.
    let live: { opacity: string; transform: string } | null = null;
    if (running) { const cs = getComputedStyle(el); live = { opacity: cs.opacity, transform: cs.transform }; }
    prev?.cancel();
    if (reduced()) {
      anim.current = el.animate([{ opacity: live ? live.opacity : "0" }, { opacity: "1" }], { duration: m.reducedDuration, easing: "linear" });
      return;
    }
    const easing = m.easing.startsWith("--") ? getComputedStyle(el).getPropertyValue(m.easing).trim() || "ease-out" : m.easing;
    const from = live
      ? { opacity: live.opacity, transform: live.transform }
      : { opacity: "0", transform: dir ? `translateX(${dir * m.offset}px)` : "none" };
    anim.current = el.animate([from, { opacity: "1", transform: "none" }], { duration: m.duration, easing });
    // `change` is the trigger; `dir` and `m` are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [change]);
  useLayoutEffect(() => () => anim.current?.cancel(), []);
}
